import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Check, ArrowRight } from 'lucide-react';
import PageHero from '../components/ui/PageHero';
import SEO from '../components/SEO';
import { serviceLandings, serviceLandingList } from '../data/mock';

const ServiceLandingPage = ({ slug }) => {
  const page = serviceLandings[slug];

  if (!page) {
    return <Navigate to="/notre-offre" replace />;
  }

  const related = serviceLandingList.filter((p) => p.slug !== slug);

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: page.heroTitle,
      description: page.seoDescription,
      provider: {
        '@type': 'Organization',
        name: 'Mon Evénemenciel',
        url: 'https://monevenemenciel.eu',
      },
      areaServed: { '@type': 'Country', name: 'France' },
      url: `https://monevenemenciel.eu${page.path}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faq.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Accueil',
          item: 'https://monevenemenciel.eu/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Notre offre',
          item: 'https://monevenemenciel.eu/notre-offre',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: page.heroTitle,
          item: `https://monevenemenciel.eu${page.path}`,
        },
      ],
    },
  ];

  return (
    <div>
      <SEO
        title={page.seoTitle}
        description={page.seoDescription}
        keywords={page.seoKeywords}
        url={page.path}
        image={page.heroImage}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <PageHero
        title={page.heroTitle}
        subtitle={page.heroSubtitle}
        backgroundImage={page.heroImage}
      />

      <section className="py-14 md:py-20 bg-brand-ivory">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-brand-terracotta mb-3">
            Notre expertise
          </p>
          <h2 className="font-display text-3xl md:text-4xl text-brand-petrol mb-6">
            {page.introTitle}
          </h2>
          {page.introText.map((paragraph) => (
            <p
              key={paragraph.slice(0, 32)}
              className="text-brand-petrol/70 leading-relaxed mb-4 text-sm md:text-base"
            >
              {paragraph}
            </p>
          ))}
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {page.benefits.map((b) => (
              <div key={b.title} className="border-t border-brand-terracotta/40 pt-6">
                <h3 className="font-display text-xl text-brand-petrol mb-2">{b.title}</h3>
                <p className="text-brand-petrol/70 text-sm leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-brand-ivory">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl text-brand-petrol text-center mb-10">
            Ce que nous proposons
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {page.offerings.map((item) => (
              <li
                key={item}
                className="flex items-center gap-3 text-brand-petrol/80 text-sm md:text-base"
              >
                <Check className="w-5 h-5 text-brand-terracotta flex-shrink-0" strokeWidth={1.5} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl text-brand-petrol text-center mb-10">
            Questions fréquentes
          </h2>
          <div className="space-y-8">
            {page.faq.map((item) => (
              <div key={item.q}>
                <h3 className="font-display text-xl text-brand-petrol mb-2">{item.q}</h3>
                <p className="text-brand-petrol/70 text-sm md:text-base leading-relaxed">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-brand-petrol text-center text-white">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="font-display text-3xl md:text-4xl mb-4">Prêt à en parler ?</h2>
          <p className="text-white/65 mb-8 font-display italic text-lg">
            L&apos;art de vous rassembler, concrètement.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-brand-terracotta text-white px-8 py-3.5 rounded-md font-medium hover:bg-[#7a3826] transition-colors"
          >
            {page.ctaLabel}
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      <section className="py-12 bg-brand-ivory border-t border-brand-petrol/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-brand-terracotta mb-4">
            Continuer à explorer
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={r.path}
                className="text-brand-petrol hover:text-brand-terracotta font-display text-lg underline-offset-4 hover:underline"
              >
                {r.navLabel}
              </Link>
            ))}
            <Link
              to="/notre-offre"
              className="text-brand-petrol hover:text-brand-terracotta font-display text-lg underline-offset-4 hover:underline"
            >
              Toute l&apos;offre
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceLandingPage;
